/**
 * L'orbite — la troisième expérience, et celle qui **justifie** l'intégrateur.
 *
 * SPEC §9 le disait déjà, et il vaut la peine d'être précis : sous pesanteur
 * constante, la chute libre a une solution polynomiale que velocity Verlet
 * reproduit exactement. Le noyau y était exercé, pas nécessaire. Ici la
 * pesanteur varie avec la distance ; il n'existe aucune forme close
 * élémentaire donnant la position en fonction du temps, et l'équation de
 * Kepler elle-même ne se résout que par itération. Le pas à pas n'est plus une
 * façon de faire parmi d'autres, c'est la seule.
 *
 * ## Les deux modèles
 *
 * - **Newton** — l'attraction décroît comme le carré de la distance.
 *   **Zéro hypothèse libre** : l'exposant vaut deux parce que la théorie le
 *   dit, pas parce qu'on l'a réglé.
 * - **Loi en 1/rⁿ** — le même champ, l'exposant laissé libre.
 *   **Une hypothèse libre.**
 *
 * Le second **contient** le premier : à n = 2 ils prédisent rigoureusement la
 * même chose. Aucune observation ne peut donc réfuter la loi à exposant libre
 * — elle peut toujours se rabattre sur la valeur qui marche. Ce n'est pas une
 * force, c'est le même coût qu'ailleurs dans ce projet : ce qui se compte,
 * c'est ce qu'il faut lui concéder pour qu'elle rende compte de ce qu'on voit.
 *
 * ## Ce qui les sépare : la fermeture, pas la forme
 *
 * Les deux dessinent des courbes d'allure voisine sur un tour. Sur dix, la
 * différence saute aux yeux : une orbite ne se referme sur elle-même que pour
 * n = 2 ; pour tout autre exposant l'axe de l'ellipse tourne à chaque tour et
 * la trajectoire dessine une rosace. C'est le théorème de Bertrand, et c'est
 * l'observable de cette expérience — celle que Le Verrier a mesurée sur
 * Mercure, et qui a fini par mettre Newton en défaut.
 *
 * ## Une décision qui rend la comparaison honnête
 *
 * L'exposant libre ne change que la **décroissance**, jamais l'intensité au
 * point de départ : le champ est calé pour donner exactement l'accélération de
 * Newton à la distance initiale. Sans ce calage, pousser n changerait à la
 * fois la loi et la force qu'elle exerce ici et maintenant, et on ne saurait
 * plus laquelle des deux on observe. C'est aussi ce qui rend `GM/r²·(r₀/r)^(n−2)`
 * dimensionnellement sain, là où `GM/rⁿ` ne l'est pas.
 */

import { coreFr } from "../i18n/core-fr.js";
import { PI, atan2, max, pow, round, sqrt } from "./math.js";
import type { Body, Field, Vector2 } from "./integrator.js";
import { step } from "./integrator.js";
import type { FreeHypotheses, ModelDescriptor } from "./models.js";
import type { Quantity } from "./units.js";
import { dimensionless, quantity, valueIn } from "./units.js";

export type OrbitModel =
  | (ModelDescriptor<"newton-orbit"> & { readonly law: "inverse-square" })
  | (ModelDescriptor<"tuned-orbit"> & { readonly law: "tuned-power" });

/**
 * Constante de gravitation, en m³·kg⁻¹·s⁻².
 *
 * **Référence externe**, jamais un arbitre : c'est une valeur publiée (CODATA),
 * que ce projet n'a pas mesurée. Elle entre à l'identique dans les deux
 * modèles, donc elle ne peut en favoriser aucun.
 */
const G = 6.6743e-11;

// ── Les deux modèles ─────────────────────────────────────────────────────────

export const NEWTON_ORBIT: OrbitModel = {
  id: "newton-orbit",
  law: "inverse-square",
  label: coreFr.orbit.newtonLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(0) } satisfies FreeHypotheses,
  assumptions: [
    coreFr.orbit.massProportionalAttraction,
    coreFr.orbit.inverseSquareAttraction,
    coreFr.orbit.fixedCentralBody,
  ],
};

export const TUNED_ORBIT: OrbitModel = {
  id: "tuned-orbit",
  law: "tuned-power",
  label: coreFr.orbit.powerLawLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(1) } satisfies FreeHypotheses,
  assumptions: [
    coreFr.orbit.powerLawAttraction,
    coreFr.orbit.freeExponent,
    coreFr.orbit.sameInitialAttraction,
  ],
};

export interface OrbitSetup {
  readonly centralMass: Quantity<"mass">;
  /** Distance initiale au centre. Le corps y part perpendiculairement. */
  readonly distance: Quantity<"length">;
  readonly speed: Quantity<"velocity">;
  /** L'hypothèse libre : l'exposant de décroissance. Newton l'ignore. */
  readonly exponent: Quantity<"dimensionless">;
}

/** Newton fixe l'exposant à deux ; l'autre modèle le lit dans les réglages. */
function exponentOf(model: OrbitModel, setup: OrbitSetup): number {
  return model.law === "inverse-square" ? 2 : valueIn(setup.exponent, "1");
}

// ── Le champ ─────────────────────────────────────────────────────────────────

/**
 * Rayon en deçà duquel on cesse de creuser le puits.
 *
 * Un point matériel donne une accélération infinie au centre, ce qui n'est pas
 * une physique mais une division par zéro. On borne, et on le dit : sous ce
 * rayon la scène ne veut plus rien dire de toute façon.
 */
const MINIMUM_RADIUS = 1;

export function orbitField(model: OrbitModel, setup: OrbitSetup): Field {
  const mu = G * valueIn(setup.centralMass, "kg");
  const reference = max(MINIMUM_RADIUS, valueIn(setup.distance, "m"));
  const exponent = exponentOf(model, setup);

  return (position: Vector2<"length">) => {
    const x = valueIn(position.x, "m");
    const y = valueIn(position.y, "m");
    const radius = max(MINIMUM_RADIUS, sqrt(x * x + y * y));

    // a = GM/r² · (r₀/r)^(n−2), calée sur Newton à la distance de référence.
    const strength = (mu / (radius * radius)) * pow(reference / radius, exponent - 2);

    return {
      x: quantity((-strength * x) / radius, "m/s2"),
      y: quantity((-strength * y) / radius, "m/s2"),
    };
  };
}

// ── Références analytiques ───────────────────────────────────────────────────

/** `v = √(GM/r)` : la vitesse qui équilibre exactement l'attraction. */
export function circularSpeed(
  centralMass: Quantity<"mass">,
  distance: Quantity<"length">,
): Quantity<"velocity"> {
  const radius = max(MINIMUM_RADIUS, valueIn(distance, "m"));
  return quantity(sqrt((G * valueIn(centralMass, "kg")) / radius), "m/s");
}

/** `T = 2π√(a³/GM)` — la troisième loi de Kepler, pour l'orbite circulaire. */
export function keplerPeriod(
  centralMass: Quantity<"mass">,
  distance: Quantity<"length">,
): Quantity<"time"> {
  const radius = max(MINIMUM_RADIUS, valueIn(distance, "m"));
  const mu = G * valueIn(centralMass, "kg");
  return quantity(2 * PI * sqrt((radius * radius * radius) / mu), "s");
}

// ── Le chemin ────────────────────────────────────────────────────────────────

export interface OrbitPoint {
  readonly time: Quantity<"time">;
  readonly position: Vector2<"length">;
  readonly radius: Quantity<"length">;
}

/** Pas d'intégration par échantillon rendu. Le tracé reste lisse, le calcul honnête. */
const STEPS_PER_SAMPLE = 8;

/**
 * La trajectoire, échantillonnée à pas constant.
 *
 * Le chemin est calculé **une fois par jeu de réglages**, pas à chaque image :
 * une orbite ne se remonte pas depuis le départ soixante fois par seconde. La
 * vue y lit l'instant qu'elle veut afficher, et retrouve donc exactement les
 * mêmes positions à chaque passage — mêmes réglages, mêmes sorties.
 */
export function orbitPath(
  model: OrbitModel,
  setup: OrbitSetup,
  duration: Quantity<"time">,
  samples: Quantity<"dimensionless">,
): readonly OrbitPoint[] {
  const seconds = valueIn(duration, "s");
  const count = round(valueIn(samples, "1"));
  if (seconds <= 0 || count < 1) return [];

  const field = orbitField(model, setup);
  const dt = quantity(seconds / (count * STEPS_PER_SAMPLE), "s");

  let body: Body = {
    position: { x: setup.distance, y: quantity(0, "m") },
    velocity: { x: quantity(0, "m/s"), y: setup.speed },
    // La masse du corps en orbite n'entre pas dans le mouvement — c'est le
    // principe d'équivalence, et le champ ne la demande pas non plus.
    mass: quantity(1, "kg"),
  };

  const points: OrbitPoint[] = [pointOf(body, 0)];
  for (let index = 1; index <= count; index += 1) {
    for (let sub = 0; sub < STEPS_PER_SAMPLE; sub += 1) {
      body = step(body, field, dt);
    }
    points.push(pointOf(body, (seconds * index) / count));
  }
  return points;
}

function pointOf(body: Body, seconds: number): OrbitPoint {
  const x = valueIn(body.position.x, "m");
  const y = valueIn(body.position.y, "m");
  return {
    time: quantity(seconds, "s"),
    position: body.position,
    radius: quantity(sqrt(x * x + y * y), "m"),
  };
}

// ── La précession ────────────────────────────────────────────────────────────

/**
 * De combien l'axe de l'orbite tourne à chaque tour.
 *
 * Mesurée, pas déduite : on repère deux apoapses successifs — les maxima de
 * distance — et on compte l'angle qui les sépare, moins le tour complet. Zéro
 * veut dire que l'orbite se referme sur elle-même, ce que seul l'exposant deux
 * produit.
 *
 * Rend `undefined` quand la durée intégrée ne contient pas deux apoapses : il
 * n'y a alors rien à mesurer, et zéro serait un mensonge — « je n'ai pas vu »
 * n'est pas « il n'y en a pas ».
 */
export function precessionPerOrbit(
  model: OrbitModel,
  setup: OrbitSetup,
  duration: Quantity<"time">,
  samples: Quantity<"dimensionless">,
): Quantity<"angle"> | undefined {
  return precessionOfPath(orbitPath(model, setup, duration, samples));
}

/** Précession d'un chemin déjà intégré, partagée avec les vues et les chiffres. */
export function precessionOfPath(path: readonly OrbitPoint[]): Quantity<"angle"> | undefined {
  if (path.length < 3) return undefined;

  const angles = apoapsisAngles(path);
  if (angles.length < 2) return undefined;

  // Moyenne des écarts successifs, chacun ramené dans ]−π, π] : sans ce
  // repliement, un apoapse passant par ±π ferait sauter la mesure d'un tour.
  let total = 0;
  for (let index = 1; index < angles.length; index += 1) {
    total += wrapToPi(angles[index]! - angles[index - 1]!);
  }
  const mean = total / (angles.length - 1);
  return quantity((mean * 180) / PI, "deg");
}

/**
 * Les directions des passages au plus loin, dans l'ordre.
 *
 * Un apoapse est un maximum local de la distance : on le repère en comparant
 * chaque point à ses deux voisins, sans dériver quoi que ce soit. Helper privé,
 * donc libre d'employer des nombres nus — ce sont des angles de travail, pas
 * des grandeurs qui circulent (CLAUDE.md §1).
 */
function apoapsisAngles(path: readonly OrbitPoint[]): readonly number[] {
  const found: number[] = [];
  for (let index = 1; index < path.length - 1; index += 1) {
    const before = valueIn(path[index - 1]!.radius, "m");
    const here = valueIn(path[index]!.radius, "m");
    const after = valueIn(path[index + 1]!.radius, "m");
    if (here > before && here >= after) {
      found.push(
        atan2(valueIn(path[index]!.position.y, "m"), valueIn(path[index]!.position.x, "m")),
      );
    }
  }
  return found;
}

function wrapToPi(angle: number): number {
  const turns = 2 * PI;
  let wrapped = angle % turns;
  if (wrapped > PI) wrapped -= turns;
  if (wrapped <= -PI) wrapped += turns;
  return wrapped;
}
